import { Navigate, useParams } from "react-router-dom";
import { Footer, Header } from "@/components";
import { NewsContent } from "@/components/NewsContent";
import { NewsDetailHeader } from "@/components/NewsDetailHeader";
import { NewsDetailSkeleton } from "@/components/NewsDetailSkeleton";
import { NewsHeroImage } from "@/components/NewsHeroImage";
import SchemaBreadcrumb from "@/components/SchemaBreadcrumb";
import SchemaNewsArticle from "@/components/SchemaNewsArticle";
import SEOHead from "@/components/SEOHead";
import { ShareButtons } from "@/components/ShareButtons";
import { useNewsDetail } from "@/hooks/use-news-detail";
import { toBrazilianDateIso } from "@/lib/date-utils";
import { copyLinkToClipboard } from "@/lib/share-utils";

const NewsDetail = () => {
	const { id } = useParams<{ id: string }>();
	const { news, loading, error } = useNewsDetail(id);

	if (loading) {
		return (
			<div className="min-h-screen flex flex-col bg-background">
				<Header transparent />
				<main className="flex-1">
					<NewsDetailSkeleton />
				</main>
			</div>
		);
	}

	if (error || !news) {
		return <Navigate to="/404" replace />;
	}

	const description = `${news.newsContent.substring(0, 160).replace(/\s+/g, " ").trim()}...`;

	const publishedTime = toBrazilianDateIso(news.newsDate);

	// Extract keywords from news title and content for better SEO
	const extractKeywords = (title: string, _newsContent: string): string[] => {
		const keywords: string[] = ["CIB", "Cadastro Imobiliário", "Notícias"];

		// Add title words that are relevant (filter out common words)
		const titleWords = title
			.toLowerCase()
			.replace(/[^\w\sàáâãéêíóôõúç]/g, "")
			.split(/\s+/)
			.filter(
				(word) =>
					word.length > 3 &&
					!["sobre", "para", "como", "qual", "essa", "este", "essa"].includes(
						word,
					),
			);

		// Add unique meaningful words from title
		titleWords.slice(0, 3).forEach((word) => {
			const capitalized = word.charAt(0).toUpperCase() + word.slice(1);
			if (!keywords.includes(capitalized)) {
				keywords.push(capitalized);
			}
		});

		return keywords.slice(0, 6); // Limit to 6 keywords
	};

	const articleTags = extractKeywords(news.newsTitle, news.newsContent);

	return (
		<div className="min-h-screen flex flex-col bg-background animate-fade-in">
			<SEOHead
				title={news.newsTitle}
				description={description}
				keywords={`notícias CIB, ${news.newsTitle}, ${articleTags.join(", ")}, cadastro imobiliário brasileiro, atualização imobiliária`}
				canonicalUrl={`/noticias/${id}`}
				ogType="article"
				ogImage={news.newsImg}
				ogImageAlt={`Imagem da notícia: ${news.newsTitle}`}
				article={{
					publishedTime,
					author: news.newsAuthor,
					section: "Notícias",
					tags: articleTags,
				}}
			/>
			<SchemaBreadcrumb
				items={[
					{ name: "Início", path: "/" },
					{ name: "Notícias", path: "/noticias" },
					{ name: news.newsTitle, path: `/noticias/${id}` },
				]}
			/>
			<SchemaNewsArticle
				news={news}
				url={`/noticias/${id}`}
				publishedTime={publishedTime}
			/>
			<Header transparent />

			<main className="flex-1">
				<NewsHeroImage src={news.newsImg} alt={news.newsTitle} />

				<article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10">
					<NewsDetailHeader news={news} onCopyLink={copyLinkToClipboard} />
					<NewsContent content={news.newsContent} sourceUrl={news.newsUrl} />
					<ShareButtons
						title={news.newsTitle}
						url={news.newsUrl}
						onCopyLink={copyLinkToClipboard}
						variant="mobile"
						description={`${news.newsContent.substring(0, 150)}...`}
					/>
				</article>
			</main>

			<Footer />
		</div>
	);
};

export default NewsDetail;
